// Official-style App Store & Google Play badges linking to the live store listings.
// The App Store link adapts to the visitor's locale (e.g. /us/, /de/, /tr/); with no
// country code Apple geolocates the storefront automatically. Google Play auto-detects
// language when no hl param is present.

const APP_STORE_PATH = "app/mapcast-live/id6792511749";

export const getAppStoreUrl = () => {
  const match =
    typeof navigator !== "undefined" &&
    (navigator.language || "").match(/^[a-z]{2}-([a-zA-Z]{2})\b/);
  const cc = match ? match[1].toLowerCase() : null;
  return cc
    ? `https://apps.apple.com/${cc}/${APP_STORE_PATH}`
    : `https://apps.apple.com/${APP_STORE_PATH}`;
};

export const STORE_LINKS = {
  get appStore() {
    return getAppStoreUrl();
  },
  googlePlay:
    "https://play.google.com/store/apps/details?id=com.emergent.broadcastmonetize.goig41",
};

const AppStoreBadge = () => (
  <a
    href={getAppStoreUrl()}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 rounded-xl bg-black border border-white/20 px-5 h-14 select-none transition-colors duration-300 hover:border-mc-pink/60 hover:bg-white/5"
    data-testid="badge-app-store"
    aria-label="Download MapCast on the App Store"
  >
    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden>
      <path d="M17.05 12.53c-.02-2.14 1.75-3.17 1.83-3.22-1-1.46-2.55-1.66-3.1-1.68-1.32-.13-2.58.78-3.25.78-.67 0-1.7-.76-2.8-.74-1.44.02-2.77.84-3.51 2.13-1.5 2.6-.38 6.44 1.07 8.55.71 1.03 1.56 2.19 2.67 2.15 1.07-.04 1.48-.69 2.77-.69 1.29 0 1.66.69 2.8.67 1.16-.02 1.89-1.05 2.6-2.09.82-1.2 1.16-2.36 1.18-2.42-.03-.01-2.26-.87-2.28-3.44zM14.9 6.02c.59-.72.99-1.71.88-2.71-.85.03-1.88.57-2.49 1.28-.55.63-1.03 1.64-.9 2.61.95.07 1.92-.48 2.51-1.18z" />
    </svg>
    <div className="text-left leading-none">
      <div className="text-[10px] text-white/70 font-body">Download on the</div>
      <div className="text-lg font-body font-semibold text-white -mt-0.5">App Store</div>
    </div>
  </a>
);

const GooglePlayBadge = () => (
  <a
    href={STORE_LINKS.googlePlay}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 rounded-xl bg-black border border-white/20 px-5 h-14 select-none transition-colors duration-300 hover:border-mc-pink/60 hover:bg-white/5"
    data-testid="badge-google-play"
    aria-label="Get MapCast on Google Play"
  >
    <svg viewBox="0 0 512 512" className="w-6 h-6" aria-hidden>
      <path fill="#00D3FF" d="M47 20 300 256 47 492c-9-5-15-15-15-27V47c0-12 6-22 15-27z" />
      <path fill="#00F076" d="M47 20c4-2 9-3 14-2l236 133-52 52L47 20z" />
      <path fill="#FF3A44" d="M245 307l52 52L61 494c-5 1-10 0-14-2l198-185z" />
      <path fill="#FFC800" d="M297 153l86 48c17 10 17 30 0 40l-86 48-52-52 52-84z" />
    </svg>
    <div className="text-left leading-none">
      <div className="text-[10px] text-white/70 font-body">GET IT ON</div>
      <div className="text-lg font-body font-semibold text-white -mt-0.5">Google Play</div>
    </div>
  </a>
);

export const StoreBadges = ({ className = "" }) => (
  <div className={`flex flex-wrap items-center gap-4 ${className}`}>
    <AppStoreBadge />
    <GooglePlayBadge />
  </div>
);

const QRTile = ({ href, img, label, caption, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col items-center gap-3"
    data-testid={testid}
    aria-label={`${caption}: ${label}`}
  >
    <div className="rounded-2xl bg-white p-3 border border-white/10 shadow-[0_0_40px_-10px_rgba(217,70,239,0.4)] transition-transform duration-300 group-hover:scale-105">
      <img src={img} alt={`QR code — ${label}`} className="w-28 h-28 md:w-32 md:h-32" loading="lazy" />
    </div>
    <div className="text-center leading-tight">
      <div className="text-sm font-body font-semibold text-white">{label}</div>
      <div className="text-xs text-mc-muted font-light">{caption}</div>
    </div>
  </a>
);

export const StoreQRCodes = ({ className = "" }) => (
  <div className={`flex flex-wrap items-start justify-center gap-8 md:gap-12 ${className}`}>
    <QRTile
      href={getAppStoreUrl()}
      img="/assets/qr-app-store.svg"
      label="App Store"
      caption="Scan to download for iOS"
      testid="qr-app-store"
    />
    <QRTile
      href={STORE_LINKS.googlePlay}
      img="/assets/qr-google-play.svg"
      label="Google Play"
      caption="Scan to download for Android"
      testid="qr-google-play"
    />
  </div>
);

export default StoreBadges;
